import type { PermissionCatalog } from "@/types/role";

import { PermissionRules } from "@/features/roles/helpers/permission-rules";

// What the backend sends from `roles/catalog`, for mock mode and the tests.
export const PERMISSION_CATALOG_MOCK: PermissionCatalog = {
  permissions: [
    {
      key: "contacts.view",
      requires: [],
    },
    {
      key: "contacts.edit",
      requires: ["contacts.view"],
    },
    {
      key: "contacts.delete",
      requires: ["contacts.edit"],
    },
    {
      key: "contacts.import",
      requires: ["contacts.edit"],
    },
    {
      key: "contacts.export",
      requires: ["contacts.view"],
    },
    {
      key: "conversations.assign",
      requires: [],
    },
    {
      key: "conversations.delete",
      requires: [],
    },
    {
      key: "appointments.view",
      requires: [],
    },
    {
      key: "appointments.edit",
      requires: ["appointments.view"],
    },
    {
      key: "appointments.delete",
      requires: ["appointments.edit"],
    },
    {
      key: "payments.edit",
      requires: ["appointments.edit"],
    },
    {
      key: "payments.delete",
      requires: ["payments.edit"],
    },
    {
      key: "orders.view",
      requires: [],
    },
    {
      key: "orders.edit",
      requires: ["orders.view"],
    },
    {
      key: "orders.delete",
      requires: ["orders.edit"],
    },
    {
      key: "tickets.view",
      requires: [],
    },
    {
      key: "tickets.edit",
      requires: ["tickets.view"],
    },
    {
      key: "tickets.delete",
      requires: ["tickets.edit"],
    },
    {
      key: "channels.manage",
      requires: [],
    },
    {
      key: "channels.delete",
      requires: ["channels.manage"],
    },
    {
      key: "channels.connect",
      requires: ["channels.manage"],
    },
    {
      key: "broadcasts.view",
      requires: [],
    },
    {
      key: "broadcasts.edit",
      requires: ["broadcasts.view"],
    },
    {
      key: "broadcasts.delete",
      requires: ["broadcasts.edit"],
    },
    {
      key: "workflows.view",
      requires: [],
    },
    {
      key: "workflows.edit",
      requires: ["workflows.view"],
    },
    {
      key: "workflows.delete",
      requires: ["workflows.edit"],
    },
    {
      key: "ads.view",
      requires: [],
    },
    {
      key: "ads.edit",
      requires: ["ads.view"],
    },
    {
      key: "ads.delete",
      requires: ["ads.edit"],
    },
    {
      key: "ads.connect",
      requires: ["ads.edit"],
    },
    {
      key: "reports.view",
      requires: [],
    },
    {
      key: "reports.amount",
      requires: ["reports.view"],
    },
    {
      key: "reports.export",
      requires: ["reports.view"],
    },
    {
      key: "billing.view",
      requires: [],
    },
    {
      key: "plan.manage",
      requires: [],
    },
    {
      key: "credits.buy",
      requires: [],
    },
    {
      key: "treatments.manage",
      requires: [],
    },
    {
      key: "treatments.delete",
      requires: ["treatments.manage"],
    },
    {
      key: "store.manage",
      requires: [],
    },
    {
      key: "store.delete",
      requires: ["store.manage"],
    },
    {
      key: "echo.view",
      requires: [],
    },
    {
      key: "echo.edit",
      requires: ["echo.view"],
    },
    {
      key: "echo.knowledge",
      requires: ["echo.edit"],
    },
    {
      key: "echo.suggestions",
      requires: ["echo.edit"],
    },
    {
      key: "echo.mode",
      requires: ["echo.edit"],
    },
    {
      key: "echo.delete",
      requires: ["echo.edit"],
    },
    {
      key: "replies.manage",
      requires: [],
    },
    {
      key: "replies.delete",
      requires: ["replies.manage"],
    },
    {
      key: "views.create",
      requires: ["contacts.view"],
    },
    {
      key: "views.share",
      requires: ["views.create"],
    },
    {
      key: "inbox.manage",
      requires: [],
    },
    {
      key: "inbox.delete",
      requires: ["inbox.manage"],
    },
    {
      key: "teams.manage",
      requires: [],
    },
    {
      key: "teams.delete",
      requires: ["teams.manage"],
    },
    {
      key: "integrations.manage",
      requires: [],
    },
    {
      key: "integrations.connect",
      requires: ["integrations.manage"],
    },
    {
      key: "integrations.delete",
      requires: ["integrations.manage"],
    },
    {
      key: "workspace.settings",
      requires: [],
    },
    {
      key: "members.manage",
      requires: [],
    },
    {
      key: "members.delete",
      requires: ["members.manage"],
    },
    {
      key: "roles.manage",
      requires: [],
    },
    {
      key: "developer.manage",
      requires: [],
    },
  ],
  workspaceWide: [
    "reports.view",
    "broadcasts.view",
    "workflows.view",
    "ads.view",
    "teams.manage",
    "integrations.manage",
  ],
  withheldFromAgents: [
    "channels.manage",
    "channels.delete",
    "channels.connect",
    "broadcasts.view",
    "broadcasts.edit",
    "broadcasts.delete",
    "workflows.view",
    "workflows.edit",
    "workflows.delete",
    "ads.view",
    "ads.edit",
    "ads.delete",
    "ads.connect",
    "reports.view",
    "reports.amount",
    "reports.export",
    "billing.view",
    "plan.manage",
    "credits.buy",
    "treatments.manage",
    "treatments.delete",
    "store.manage",
    "store.delete",
    "echo.view",
    "echo.edit",
    "echo.knowledge",
    "echo.suggestions",
    "echo.mode",
    "echo.delete",
    "inbox.manage",
    "inbox.delete",
    "teams.manage",
    "teams.delete",
    "integrations.manage",
    "integrations.connect",
    "integrations.delete",
    "workspace.settings",
    "members.manage",
    "members.delete",
    "roles.manage",
    "developer.manage",
  ],
  presets: {
    "workspace:admin": [
      "contacts.view",
      "contacts.edit",
      "contacts.delete",
      "contacts.import",
      "contacts.export",
      "conversations.assign",
      "conversations.delete",
      "appointments.view",
      "appointments.edit",
      "appointments.delete",
      "payments.edit",
      "payments.delete",
      "orders.view",
      "orders.edit",
      "orders.delete",
      "tickets.view",
      "tickets.edit",
      "tickets.delete",
      "channels.manage",
      "channels.delete",
      "channels.connect",
      "broadcasts.view",
      "broadcasts.edit",
      "broadcasts.delete",
      "workflows.view",
      "workflows.edit",
      "workflows.delete",
      "ads.view",
      "ads.edit",
      "ads.delete",
      "ads.connect",
      "reports.view",
      "reports.amount",
      "reports.export",
      "billing.view",
      "plan.manage",
      "credits.buy",
      "treatments.manage",
      "treatments.delete",
      "store.manage",
      "store.delete",
      "echo.view",
      "echo.edit",
      "echo.knowledge",
      "echo.suggestions",
      "echo.mode",
      "echo.delete",
      "replies.manage",
      "replies.delete",
      "views.create",
      "views.share",
      "inbox.manage",
      "inbox.delete",
      "teams.manage",
      "teams.delete",
      "integrations.manage",
      "integrations.connect",
      "integrations.delete",
      "workspace.settings",
      "members.manage",
      "members.delete",
      "roles.manage",
      "developer.manage",
    ],
    "workspace:member": [
      "contacts.view",
      "contacts.edit",
      "contacts.delete",
      "contacts.import",
      "contacts.export",
      "conversations.assign",
      "conversations.delete",
      "appointments.view",
      "appointments.edit",
      "appointments.delete",
      "payments.edit",
      "orders.view",
      "orders.edit",
      "orders.delete",
      "tickets.view",
      "tickets.edit",
      "tickets.delete",
      "channels.manage",
      "broadcasts.view",
      "broadcasts.edit",
      "broadcasts.delete",
      "workflows.view",
      "workflows.edit",
      "workflows.delete",
      "ads.view",
      "ads.edit",
      "plan.manage",
      "treatments.manage",
      "treatments.delete",
      "store.manage",
      "store.delete",
      "echo.view",
      "echo.edit",
      "echo.delete",
      "replies.manage",
      "replies.delete",
      "inbox.manage",
      "inbox.delete",
      "teams.manage",
      "teams.delete",
      "integrations.manage",
      "views.create",
      "views.share",
    ],
    "workspace:agent": [
      "contacts.view",
      "contacts.edit",
      "conversations.assign",
      "appointments.view",
      "appointments.edit",
      "payments.edit",
      "orders.view",
      "orders.edit",
      "replies.manage",
      "replies.delete",
      "views.create",
    ],
    "workspace:marketing": [
      "contacts.view",
      "contacts.edit",
      "contacts.delete",
      "contacts.import",
      "contacts.export",
      "conversations.assign",
      "conversations.delete",
      "appointments.view",
      "appointments.edit",
      "appointments.delete",
      "payments.edit",
      "orders.view",
      "orders.edit",
      "orders.delete",
      "tickets.view",
      "tickets.edit",
      "tickets.delete",
      "channels.manage",
      "broadcasts.view",
      "broadcasts.edit",
      "broadcasts.delete",
      "workflows.view",
      "workflows.edit",
      "workflows.delete",
      "ads.view",
      "ads.edit",
      "ads.delete",
      "reports.view",
      "reports.export",
      "treatments.manage",
      "treatments.delete",
      "store.manage",
      "store.delete",
      "echo.view",
      "echo.edit",
      "echo.delete",
      "replies.manage",
      "replies.delete",
      "inbox.manage",
      "inbox.delete",
      "teams.manage",
      "teams.delete",
      "integrations.manage",
      "views.create",
      "views.share",
    ],
    "workspace:finance": [
      "contacts.view",
      "contacts.edit",
      "contacts.delete",
      "contacts.import",
      "contacts.export",
      "conversations.assign",
      "conversations.delete",
      "appointments.view",
      "appointments.edit",
      "appointments.delete",
      "payments.edit",
      "payments.delete",
      "orders.view",
      "orders.edit",
      "orders.delete",
      "tickets.view",
      "tickets.edit",
      "tickets.delete",
      "broadcasts.view",
      "broadcasts.edit",
      "broadcasts.delete",
      "workflows.view",
      "workflows.edit",
      "workflows.delete",
      "ads.view",
      "ads.edit",
      "reports.view",
      "reports.amount",
      "reports.export",
      "billing.view",
      "plan.manage",
      "credits.buy",
      "treatments.manage",
      "treatments.delete",
      "store.manage",
      "store.delete",
      "echo.view",
      "views.create",
    ],
  },
};

export const MOCK_PERMISSION_RULES = new PermissionRules(
  PERMISSION_CATALOG_MOCK,
);
